import React, { useState, useEffect } from 'react';
import { Form, Button, Alert } from '@/components/ui';
import { workIdService, type WorkIdDetail } from '../../../services/workIdService';
import ResponsiveModal from '../../../components/ResponsiveModal';

interface WorkIdCloseWithoutActionModalProps {
  visible: boolean;
  onClose: () => void;
  workId: WorkIdDetail | null;
  onSuccess: () => void;
}

const WorkIdCloseWithoutActionModal: React.FC<WorkIdCloseWithoutActionModalProps> = ({
  visible,
  onClose,
  workId,
  onSuccess,
}) => {
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (visible) {
      setComment('');
      setError(null);
    }
  }, [visible, workId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!workId) return;

    setError(null);
    setSubmitting(true);

    try {
      await workIdService.closeWithoutAction(workId.id, comment.trim() || undefined);
      onClose();
      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to close work ID');
    } finally {
      setSubmitting(false);
    }
  };

  if (!workId) return null;

  const title = `Close without action — ${workId.workIdentificationId ?? workId.id}`;

  return (
    <ResponsiveModal
      isOpen={visible}
      onClose={onClose}
      title={title}
      size="medium"
      footer={
        <>
          <Button type="button" variant="secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button type="button" variant="primary" onClick={handleSubmit} disabled={submitting}>
            {submitting ? 'Closing…' : 'Close without action'}
          </Button>
        </>
      }
    >
      <p className="tw-text-content-muted tw-text-sm tw-mb-3">
        This sets the Work ID to <strong>Closed without action</strong> (no task assignment or task closure).
        Only Low or Medium priority Work IDs in New or Approved status can be closed this way.
      </p>
      {error && <Alert variant="danger" className="tw-py-2 tw-text-sm">{error}</Alert>}
      <Form onSubmit={handleSubmit}>
        <Form.Group className="tw-mb-0">
          <Form.Label>Comment <span className="tw-text-content-muted tw-font-normal">(optional)</span></Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Add context for the audit log…"
            maxLength={2000}
            disabled={submitting}
          />
        </Form.Group>
      </Form>
    </ResponsiveModal>
  );
};

export default WorkIdCloseWithoutActionModal;
